/**
 * ws.ts — reconnecting WebSocket client with TEXT (JSON) + BINARY (media) lanes.
 * Mirrors prochat's ws.ts, extended to send/receive binary media frames.
 *
 * URL derives from page origin (same-origin upgrade) or VITE_VIDCHAT_WS_URL.
 */
export type WSState = 'connecting' | 'open' | 'closed' | 'error';

export interface WSClient {
    /** Send a JSON signaling frame. */
    send: (frame: object) => void;
    /** Send a raw binary media frame. */
    sendBinary: (data: Uint8Array) => void;
    close: () => void;
    onmessage: (handler: (frame: unknown) => void) => void;
    onbinary: (handler: (data: Uint8Array) => void) => void;
    onstate: (handler: (s: WSState) => void) => void;
}

export function defaultUrl(): string {
    const env = (import.meta.env.VITE_VIDCHAT_WS_URL as string | undefined);
    if (env) return env;
    if (typeof location === 'undefined') return 'ws://127.0.0.1:8087/vidchat/ws';
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${location.host}/vidchat/ws`;
}

export function connect(url = defaultUrl()): WSClient {
    let ws: WebSocket | null = null;
    let closedByUser = false;
    let backoff = 500;
    const outbox: string[] = [];
    let messageHandler: ((f: unknown) => void) | null = null;
    let binaryHandler: ((d: Uint8Array) => void) | null = null;
    let stateHandler: ((s: WSState) => void) | null = null;

    function setState(s: WSState) { stateHandler?.(s); }

    function open() {
        setState('connecting');
        ws = new WebSocket(url);
        ws.binaryType = 'arraybuffer';
        ws.onopen = () => {
            backoff = 500;
            setState('open');
            for (const f of outbox) ws!.send(f);
            outbox.length = 0;
        };
        ws.onmessage = (e) => {
            if (typeof e.data === 'string') {
                if (!messageHandler) return;
                try { messageHandler(JSON.parse(e.data)); } catch { /* ignore */ }
            } else if (e.data instanceof ArrayBuffer) {
                binaryHandler?.(new Uint8Array(e.data));
            }
        };
        ws.onerror = () => { setState('error'); };
        ws.onclose = () => {
            setState('closed');
            if (closedByUser) return;
            setTimeout(open, backoff);
            backoff = Math.min(backoff * 2, 8000);
        };
    }
    open();

    return {
        send: (frame) => {
            const s = JSON.stringify(frame);
            if (ws && ws.readyState === ws.OPEN) ws.send(s);
            else outbox.push(s);
        },
        sendBinary: (data) => {
            if (ws && ws.readyState === ws.OPEN) ws.send(data);
            // media is droppable; if not open we simply skip (no buffering)
        },
        close: () => {
            closedByUser = true;
            try { ws?.close(1000, 'client-close'); } catch { /* ignore */ }
        },
        onmessage: (h) => { messageHandler = h; },
        onbinary: (h) => { binaryHandler = h; },
        onstate: (h) => { stateHandler = h; },
    };
}
